// Schema del modelo de captura por corrida (tiendas). Mirror de la tabla
// `registros_tienda` (migración 00032). Modelo ORTOGONAL a registros_produccion_hora:
// 1 SKU × 1 destino × [hora_inicio → hora_fin] de duración LIBRE.
// Ver docs/spec-mvp/modelo/captura-tiendas.md
//
// Validaciones replicadas desde los CHECK del DDL:
//   - cantidad_unidades > 0
//   - hora_fin > hora_inicio
//   - numero_operarios entre 1 y 60 (nullable; soporta costo con paralelismo)
//   - cantidad_stickers / cantidad_termos >= 0 (nullable)
//   - razon_fuera_turno length >= 20 si es_captura_fuera_turno
//   - razon_delegacion length >= 20 si capturado_por_supervisor
//
// Lo que NO viaja en el payload (lo deriva el backend, nunca por body):
//   - fecha_operativa: de hora_inicio local + turno (la función fecha_operativa()
//     del core depende de CURRENT_DATE → no sirve para corridas cargadas después
//     o que cruzan medianoche).
//   - planta_id: de la programación del líder en la línea es_tienda.
//   - supervisor_user_id: el user autenticado cuando capturado_por_supervisor.
import { z } from 'zod';
import { estadoRegistroSchema } from './registro_produccion_hora.js';
/**
 * Payload para crear UNA corrida. hora_inicio/hora_fin en "fake-UTC" (hora local
 * del tenant etiquetada Z, igual que el resto del sistema — el FE las compone con
 * combinarFechaHoraTurno de turno.ts).
 */
export const registroTiendaCreateSchema = z
    .object({
    tienda_id: z.string().uuid(),
    sku_id: z.string().uuid(),
    turno_id: z.string().uuid().nullable().default(null),
    hora_inicio: z.string().datetime({ offset: true }),
    hora_fin: z.string().datetime({ offset: true }),
    // Orden de producción (obligatoria). D9 (2026-07-03): las OC de tiendas
    // son numeración SAP de 10 dígitos (4503…) — se exige el formato en la
    // captura NUEVA. El histórico fuera de formato queda como está.
    orden_produccion: z
        .string()
        .trim()
        .regex(/^\d{10}$/, 'La orden de tiendas debe tener 10 dígitos (ej. 4503607602)'),
    cantidad_unidades: z.number().positive('Debe ser mayor a 0'),
    // Preparado para economía futura (opcional). numero_operarios soporta el
    // costo correcto con corridas paralelas (cuadrilla repartida).
    numero_operarios: z
        .number()
        .int()
        .min(1, 'Mínimo 1')
        .max(60, 'Máximo 60')
        .nullable()
        .default(null),
    cantidad_stickers: z.number().int().min(0).nullable().default(null),
    cantidad_termos: z.number().int().min(0).nullable().default(null),
    es_captura_fuera_turno: z.boolean().default(false),
    razon_fuera_turno: z.string().trim().max(500).nullable().default(null),
    capturado_por_supervisor: z.boolean().default(false),
    razon_delegacion: z.string().trim().max(500).nullable().default(null),
    // Si capturado_por_supervisor=true, el líder a quien se le delega.
    lider_asignado_user_id: z.string().uuid().nullable().default(null),
})
    .refine((r) => new Date(r.hora_fin).getTime() > new Date(r.hora_inicio).getTime(), {
    message: 'hora_fin debe ser posterior a hora_inicio',
    path: ['hora_fin'],
})
    .refine((r) => !r.es_captura_fuera_turno ||
    (r.razon_fuera_turno !== null && r.razon_fuera_turno.length >= 20), {
    message: 'Razón fuera de turno obligatoria (mínimo 20 caracteres)',
    path: ['razon_fuera_turno'],
})
    .refine((r) => !r.capturado_por_supervisor ||
    (r.razon_delegacion !== null && r.razon_delegacion.length >= 20), {
    message: 'Razón de delegación obligatoria (mínimo 20 caracteres)',
    path: ['razon_delegacion'],
})
    .refine((r) => !r.capturado_por_supervisor || r.lider_asignado_user_id !== null, {
    message: 'lider_asignado_user_id requerido cuando capturado_por_supervisor',
    path: ['lider_asignado_user_id'],
});
/**
 * Captura por planilla: varias corridas (incluso solapadas/paralelas) en una sola
 * transacción atómica (patrón bulk de importar matriz de capacidades).
 */
export const registrosTiendaBulkCreateSchema = z.object({
    corridas: z
        .array(registroTiendaCreateSchema)
        .min(1, 'Al menos una corrida')
        .max(100, 'Máximo 100 corridas por envío'),
});
/**
 * Edición de UNA corrida por el líder dueño (PATCH /:id). Todos los campos
 * opcionales (se actualiza solo lo enviado), al menos uno. Misma ventana que
 * líneas (turno + gracia) — la valida el backend, no el cliente.
 */
export const registroTiendaEditarSchema = z
    .object({
    tienda_id: z.string().uuid().optional(),
    sku_id: z.string().uuid().optional(),
    hora_inicio: z.string().datetime({ offset: true }).optional(),
    hora_fin: z.string().datetime({ offset: true }).optional(),
    orden_produccion: z
        .string()
        .trim()
        .regex(/^\d{10}$/, 'La orden de tiendas debe tener 10 dígitos (ej. 4503607602)')
        .optional(),
    cantidad_unidades: z.number().positive('Debe ser mayor a 0').optional(),
    numero_operarios: z
        .number()
        .int()
        .min(1, 'Mínimo 1')
        .max(60, 'Máximo 60')
        .nullable()
        .optional(),
    cantidad_stickers: z.number().int().min(0).nullable().optional(),
    cantidad_termos: z.number().int().min(0).nullable().optional(),
})
    .refine((o) => Object.keys(o).length > 0, { message: 'Debe modificar al menos un campo' })
    .refine((o) => !(o.hora_inicio && o.hora_fin) ||
    new Date(o.hora_fin).getTime() > new Date(o.hora_inicio).getTime(), { message: 'hora_fin debe ser posterior a hora_inicio', path: ['hora_fin'] });
/** Invalidación (soft-delete) de una corrida. razón ≥20 (CHECK de BD). */
export const registroTiendaInvalidarSchema = z.object({
    razon_invalidacion: z.string().trim().min(20, 'Razón obligatoria (mínimo 20 caracteres)'),
});
/** Corrida persistida (lo que devuelve el backend). */
export const registroTiendaSchema = z.object({
    id: z.string().uuid(),
    tenant_id: z.string().uuid(),
    planta_id: z.string().uuid(),
    tienda_id: z.string().uuid(),
    sku_id: z.string().uuid(),
    fecha_operativa: z.string().date(),
    turno_id: z.string().uuid().nullable(),
    hora_inicio: z.string().datetime({ offset: true }),
    hora_fin: z.string().datetime({ offset: true }),
    orden_produccion: z.string().nullable(),
    duracion_minutos: z.number(),
    cantidad_unidades: z.number(),
    productividad_unds_min: z.number(),
    numero_operarios: z.number().int().nullable(),
    cantidad_stickers: z.number().int().nullable(),
    cantidad_termos: z.number().int().nullable(),
    capturado_por_user_id: z.string().uuid(),
    lider_asignado_user_id: z.string().uuid().nullable(),
    supervisor_user_id: z.string().uuid().nullable(),
    es_captura_fuera_turno: z.boolean(),
    razon_fuera_turno: z.string().nullable(),
    capturado_por_supervisor: z.boolean(),
    razon_delegacion: z.string().nullable(),
    validado_por_user_id: z.string().uuid().nullable(),
    validado_at: z.string().datetime({ offset: true }).nullable(),
    estado: estadoRegistroSchema,
    invalidado_at: z.string().datetime({ offset: true }).nullable(),
    invalidado_por_user_id: z.string().uuid().nullable(),
    razon_invalidacion: z.string().nullable(),
    // Enriquecidos por el handler (no son columnas):
    tienda_nombre: z.string().optional(),
    sku_descripcion: z.string().optional(),
    sku_codigo_cliente: z.string().optional(),
    // Vista del supervisor (GET /supervisor): quién es el líder de la corrida.
    lider_nombre: z.string().nullable().optional(),
    lider_apellido: z.string().nullable().optional(),
    // Config de insumos del SKU en este destino (tienda_skus): cuántos stickers/
    // termos lleva CADA unidad — patrón de control del supervisor (unidades ×
    // por_unidad ≈ capturado). NULL = sin config.
    stickers_por_unidad: z.number().nullable().optional(),
    termos_por_unidad: z.number().nullable().optional(),
    // Meta aplicable = COALESCE(meta del SKU en skus_tienda_estandar, default planta 500/h).
    meta_unds_hora: z.number().nullable().optional(),
    // Cumplimiento = productividad_unds_min ÷ (meta_unds_hora / 60) × 100.
    cumplimiento_pct: z.number().nullable().optional(),
    // El líder dueño puede editar/invalidar solo si NOW() cae en la ventana del
    // turno + gracia (mismo criterio que líneas). Lo computa el backend.
    editable_por_lider: z.boolean().optional(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
});
/** Query de las corridas del día (lista del líder / polling). */
export const registrosTiendaHoyQuerySchema = z.object({
    tienda_id: z.string().uuid().optional(),
});
/**
 * Query de la vista del SUPERVISOR (GET /supervisor): corridas de las plantas
 * de su scope en una fecha operativa (default: hoy), para validar/invalidar
 * con la misma lógica que las capturas de líneas.
 */
export const registrosTiendaSupervisorQuerySchema = z.object({
    fecha: z.string().date().optional(),
});
//# sourceMappingURL=registro_tienda.js.map